import { useState, useEffect } from 'react'
import s from './MarginAnalysis.scss'
import {
  makeReportsPostRequest,
  getReportsBaseUrl,
  makePatchRequest,
  makeGetRequest
} from 'utils/api'
import { toUsers } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker, SwitchToggle } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import { FaFileExcel, FaAngleRight, FaAngleDown, FaInfoCircle } from 'react-icons/fa'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip } from 'reactstrap'
import currency from 'currency.js'
import Select from 'react-select'
import { toTeams } from 'utils/serializers'
import { BID_SUBMISSION_STATUS } from 'utils/constants'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const columns = [
  {
    title: 'Status',
    field: 'status',
    width: '200px',
    sorting: true,
    editable: 'never'
  },
  {
    title: 'Bid Due Date',
    field: 'bidDueDate',
    sorting: true,
    width: '120px',
    editable: 'never',
    render: row => (row.bidDueDate ? timezoneDate(row.bidDueDate) : null),
    exportTransformer: row => (row.bidDueDate ? timezoneDate(row.bidDueDate) : null)
  },
  {
    title: 'Submission Status',
    field: 'submissionStatus',
    sorting: true,
    width: '200px',
    editable: 'never',
    render: row => BID_SUBMISSION_STATUS[row.submissionStatus],
    exportTransformer: row => BID_SUBMISSION_STATUS[row.submissionStatus]
  },
  {
    title: 'Client(s)',
    field: 'companies',
    sorting: true,
    width: '200px',
    editable: 'never',
    render: row => row.companies.join(', '),
    exportTransformer: row => row.companies.join(', ')
  },
  {
    title: 'Opportunity Name',
    field: 'name',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
        {row.name}
      </a>
    )
  },
  {
    title: 'Quote #',
    field: 'quoteNumber',
    width: '150px',
    sorting: true,
    editable: 'never'
  },
  {
    title: 'Project #',
    field: 'projectNumber',
    width: '150px',
    sorting: true,
    editable: 'never'
  },
  {
    title: 'Opportunity Value',
    field: 'value',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.value}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.value })
  },
  {
    title: 'Engineering',
    field: 'engineering',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.engineering}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.engineering })
  },
  {
    title: 'Engineering Hours',
    field: 'engineeringHours',
    width: '120px',
    emptyValue: '(empty)',
    sorting: true
  },
  {
    title: 'Labor',
    field: 'labor',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.labor}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.labor })
  },
  {
    title: 'Labor Hours',
    field: 'laborHours',
    width: '120px',
    emptyValue: '(empty)',
    sorting: true
  },
  {
    title: 'Materials',
    field: 'materials',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.materials}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.materials })
  },
  {
    title: 'Equipment',
    field: 'equipment',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.equipment}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.equipment })
  },
  {
    title: 'Subcontractors',
    field: 'subcontractors',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.subcontractors}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.subcontractors })
  },
  {
    title: 'Contingency',
    field: 'contingency',
    width: '120px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.contingency}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.contingency })
  },
  {
    title: 'Other Direct Job Costs',
    field: 'otherDirectJobCosts',
    width: '150px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.otherDirectJobCosts}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.otherDirectJobCosts })
  },
  {
    title: 'Total Direct Job Costs',
    field: 'totalDirectJobCosts',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.totalDirectJobCosts}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.totalDirectJobCosts })
  },
  {
    title: 'Gross Profit',
    field: 'grossProfit',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.grossProfit}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.grossProfit })
  },
  {
    title: 'Indirect Job Costs (General Conditions)',
    field: 'indirectJobCosts',
    width: '200px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.indirectJobCosts}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.indirectJobCosts })
  },
  {
    title: 'Indirect Job Costs Hours',
    field: 'indirectJobCostHours',
    width: '150px',
    emptyValue: '(empty)',
    sorting: true
  },
  {
    title: 'Construction Profit',
    field: 'constructionProfit',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.constructionProfit}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.constructionProfit })
  },
  {
    title: 'Division Overhead',
    field: 'divisionOverhead',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.divisionOverhead}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.divisionOverhead })
  },
  {
    title: 'Division Overhead %',
    field: 'divisionOverheadPercent',
    width: '150px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (row.divisionOverheadPercent ? `${row.divisionOverheadPercent}%` : null)
  },
  {
    title: 'Corporate Overhead',
    field: 'corporateOverhead',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.corporateOverhead}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.corporateOverhead })
  },
  {
    title: 'Corporate Overhead %',
    field: 'corporateOverheadPercent',
    width: '150px',
    sorting: true,
    emptyValue: '(empty)',
    render: row => (row.corporateOverheadPercent ? `${row.corporateOverheadPercent}%` : null),
    exportTransformer: row =>
      row.corporateOverheadPercent ? `${row.corporateOverheadPercent}%` : null
  },
  {
    title: 'Net Operating Income',
    field: 'netOperatingIncome',
    width: '200px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.netOperatingIncome}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.netOperatingIncome })
  },
  {
    title: 'Revenue Per Hour',
    field: 'revPerHour',
    width: '150px',
    editable: 'never',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.revPerHour}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.revPerHour })
  },
  {
    title: 'Follow-up Date',
    field: 'bidFollowUpDate',
    sorting: true,
    editable: 'never',
    width: '120px',
    render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
    exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
  },
  {
    title: 'Opportunity Manager',
    field: 'manager',
    width: '150px',
    editable: 'never',
    sorting: true
  }
]

const MarginAnalysis = () => {
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [collapse, setCollapse] = useState('')
  const [wonDateEnabled, setWonDateEnabled] = useState(false)
  const [wonStartDate, setWonStartDate] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [wonEndDate, setWonEndDate] = useState(moment().add(6, 'weeks').endOf('day').format())
  const [lostDateEnabled, setLostDateEnabled] = useState(false)
  const [lostStartDate, setLostStartDate] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [lostEndDate, setLostEndDate] = useState(moment().add(6, 'weeks').endOf('day').format())
  const [totalOppCount, setTotalOppCount] = useState(0)

  const setWonDateFilters = (dates, utc = true) => {
    setWonStartDate(utc ? utcDate(dates.from) : dates.from)
    setWonEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const setLostDateFilters = (dates, utc = true) => {
    setLostStartDate(utc ? utcDate(dates.from) : dates.from)
    setLostEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  const refreshReport = async () => {
    const result = await withLoadingLocks(
      makeReportsPostRequest('/v1/deals/margin-analysis', {
        ...(wonDateEnabled
          ? {
              wonDateFrom: wonStartDate,
              wonDateTo: wonEndDate
            }
          : {}),
        ...(lostDateEnabled
          ? {
              lostDateFrom: lostStartDate,
              lostDateTo: lostEndDate
            }
          : {}),
        ...(userFilter && userFilter.length
          ? { userFilter: userFilter.map(user => user.value) }
          : {}),
        ...(teamFilter && teamFilter.length
          ? { teamFilter: teamFilter.map(user => user.value) }
          : {})
      })
    )
    setResult(result)
    setTotalOppCount(Object.keys(result).reduce((acc, cur) => acc + result[cur].count, 0))
  }

  useEffect(() => {
    refreshReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wonDateEnabled,
    wonStartDate,
    wonEndDate,
    userFilter,
    teamFilter,
    lostDateEnabled,
    lostStartDate,
    lostEndDate
  ])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/margin-analysis/export?userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&token=${getToken()}${
          wonDateEnabled ? '&wonDateFrom=' + wonStartDate + '&wonDateTo=' + wonEndDate : ''
        }${lostDateEnabled ? '&lostDateFrom=' + lostStartDate + '&lostDateTo=' + lostEndDate : ''}`
      ),
      '_blank'
    )
  }

  const toggleCollapse = stage => {
    if (collapse !== stage) {
      setCollapse(stage)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Margin Analysis Report</h6>
      <div className={s.filters}>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setLostDateEnabled}>
            Lost Date
          </SwitchToggle>
          <RangeDatePicker
            startDate={lostStartDate}
            endDate={lostEndDate}
            onChange={setLostDateFilters}
            disabled={!lostDateEnabled}
          />
        </div>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setWonDateEnabled}>
            Won Date
          </SwitchToggle>
          <RangeDatePicker
            startDate={wonStartDate}
            endDate={wonEndDate}
            onChange={setWonDateFilters}
            disabled={!wonDateEnabled}
          />
        </div>
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 34, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 34, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      {Object.keys(result).length > 0 ? (
        <>
          <h6 className={s.subtitle}>Total: {totalOppCount} opportunities</h6>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(stage => (
              <Card className={s.card} key={stage}>
                <CardHeader onClick={() => toggleCollapse(stage)} data-event={stage}>
                  <span className={s.arrow}>
                    {collapse === stage ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {stage} ({result[stage].count}) &#8226; Value:{' '}
                  {currency(result[stage].value, { symbol: getCurrency().symbol }).format()} &#8226;
                  Gross Profit:{' '}
                  {currency(result[stage].grossProfit, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Construction Profit:{' '}
                  {currency(result[stage].constructionProfit, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Net Operating Income:{' '}
                  {currency(result[stage].netProfit, {
                    symbol: getCurrency().symbol
                  }).format()}
                </CardHeader>
                <Collapse isOpen={collapse === stage}>
                  <CardBody>
                    {collapse === stage && (
                      <MaterialTable
                        title=""
                        key={stage}
                        columns={columns}
                        data={result[stage].deals}
                        options={{
                          tableLayout: 'fixed',
                          sorting: true,
                          headerStyle: {
                            backgroundColor: '#eee',
                            color: '#000',
                            fontSize: '14px'
                          },
                          pageSize: 10,
                          padding: 'dense',
                          pageSizeOptions: [
                            5,
                            10,
                            20,
                            50,
                            { value: result[stage].deals.length, label: 'All' }
                          ],
                          emptyRowsWhenPaging: false,
                          exportMenu: [
                            {
                              label: 'Export PDF',
                              exportFunc: (cols, datas) =>
                                ExportPdf(cols, datas, 'Margin Analysis Report')
                            },
                            {
                              label: 'Export CSV',
                              exportFunc: (cols, datas) =>
                                ExportCsv(cols, datas, 'Margin Analysis Report')
                            }
                          ],
                          columnsButton: true
                        }}
                        style={{
                          boxShadow: 'none',
                          fontSize: '13px'
                        }}
                        cellEditable={{
                          onCellEditApproved: async (newValue, oldValue, rowData, columnDef) => {
                            await makePatchRequest(`/v1/deals/margin-analysis/${rowData.id}`, {
                              [columnDef.field]: newValue === '' ? null : newValue
                            })
                            refreshReport()
                          }
                        }}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try a different time range.</p>
      )}
    </>
  )
}

export default MarginAnalysis
